import {Alert, Card, FormButton, FormControl, FormField, Hyperlink} from 'platform/components';
import {UseDocumentPreviewReturnType} from 'platform/document-preview';
import {Grid, GridItem, HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isNotNil, not} from 'ramda';

import {useGetCurrenciesQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetPaymentTypeEnumQuery} from '@dms/api/finance';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  DuplicateDeliveryNote,
  GetWarehousesResponse,
  useLazyGetWarehouseQuery,
  useLazyGetWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  getOptionsFromAuthorizationProfiles,
  getOptionsFromCurrencies,
  getOptionsFromCustomersAsSupplier,
  getOptionsFromWarehouses,
  handleApiError,
  useGetSupplierCustomersQuery,
} from '@dms/shared';
import {getOptionsFromPaymentTypes} from '@dms/warehouse/shared';

import {composePath, isNotNilOrEmpty, Nullish, pushGtmEvent, useOnMount} from 'shared/web';

import {useDocumentExtractionBehaviour} from '../hooks/useDocumentExtractionBehaviour';
import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';

interface BasicInformationFormProps {
  control: FormControl<DeliveryNoteDocumentPreviewFormDefinition>;
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>;
  documentPreview: UseDocumentPreviewReturnType;
}

export function BasicInformationForm(props: BasicInformationFormProps) {
  const behaviour = useDocumentExtractionBehaviour();

  const isCollapsed = props.documentPreview.state.isCollapsed;

  const [selectedSupplier, duplicateDeliveryNote, selectedAuthorizationProfile] = useWatch({
    control: props.control,
    name: [
      'basicInformation.supplier',
      'basicInformation.duplicateDeliveryNote',
      'basicInformation.authorizationProfile',
    ],
  });

  const [availableWarehousesToSupplier, setAvailableWarehousesToSupplier] = useState<
    GetWarehousesResponse | Nullish
  >(null);

  const suppliersQuery = useGetSupplierCustomersQuery();

  const [getWarehouses, {isFetching: areWarehousesFetching}] = useLazyGetWarehousesQuery();

  const [getWarehouse, {isFetching: isWarehouseFetching}] = useLazyGetWarehouseQuery();

  const tenantQuery = useGetTenantQuery();

  const authorizationProfilesQuery = useGetAuthorizationProfilesQuery(
    {'x-tenant': tenantQuery.data?.id as string},
    {skip: isNil(tenantQuery.data?.id)}
  );

  const paymentTypesQuery = useGetPaymentTypeEnumQuery();

  const currenciesQuery = useGetCurrenciesQuery();

  const hasSuppliersLoaded = not(suppliersQuery.isLoading) && isNotNilOrEmpty(suppliersQuery.data);

  const doesSupplierExist = isNotNil(
    suppliersQuery.data?.find((supplier) => supplier.id === selectedSupplier)
  );

  const hasDuplicateDeliveryNote = isNotNilOrEmpty(duplicateDeliveryNote);

  const loadSupplierWarehouses = (supplierId: string | string[] | number | null) =>
    getWarehouses({supplierId: supplierId as string})
      .unwrap()
      .then((warehouses) => {
        if (
          behaviour.clearsWarehouseOnSupplierChange &&
          props.formApi.getValues('basicInformation.supplier') !== supplierId
        ) {
          return;
        }
        setAvailableWarehousesToSupplier(warehouses);
      })
      .catch(handleApiError);

  const handleSupplierChange = (newSupplierId: string | string[] | number | null) => {
    if (behaviour.clearsWarehouseOnSupplierChange) {
      props.formApi.setValue('basicInformation.warehouse', '');
      props.formApi.setValue('basicInformation.authorizationProfile', '');
      setAvailableWarehousesToSupplier(null);
    }
    return loadSupplierWarehouses(newSupplierId);
  };

  useOnMount(() => {
    if (!selectedSupplier) {
      return;
    }
    loadSupplierWarehouses(selectedSupplier);
  });

  const handleWarehouseChange = (newWarehouseId: string | number | null) =>
    getWarehouse({warehouseId: newWarehouseId as string})
      .unwrap()
      .then((warehouse) => {
        if (
          behaviour.clearsWarehouseOnSupplierChange &&
          props.formApi.getValues('basicInformation.warehouse') !== newWarehouseId
        ) {
          return;
        }

        props.formApi.setValue(
          'basicInformation.authorizationProfile',
          warehouse.authorizationProfileId,
          {
            shouldValidate: true,
          }
        );

        // A structured invoice already states its currency. Selecting a warehouse must not
        // relabel those amounts without an explicit currency conversion.
        if (
          behaviour.preservesDocumentCurrency &&
          props.formApi.getValues('basicInformation.currency')
        ) {
          return;
        }

        props.formApi.setValue('basicInformation.currency', warehouse.currency, {
          shouldValidate: true,
        });
      })
      .catch(handleApiError);

  const getHyperLink = (deliveryNoteNumber: string, deliveryNoteId: string): Hyperlink => ({
    leftIcon: 'action/open_in_new',
    title: deliveryNoteNumber,
    onClick: () =>
      window.open(
        composePath(warehouseRoutes.deliveryNoteDetailOverview, {params: {id: deliveryNoteId}}),
        '_blank'
      ),
  });

  const getDuplicateDeliveryNoteHyperLinks = (
    duplicateDeliveryNote: DuplicateDeliveryNote[] | Nullish
  ): Hyperlink[] | undefined => {
    if (isNil(duplicateDeliveryNote)) {
      return undefined;
    }
    return duplicateDeliveryNote.map((duplicate: DuplicateDeliveryNote) =>
      getHyperLink(
        duplicate.deliveryNoteNumber ??
          i18n.t('entity.documentExtraction.notifications.duplicateDeliveryNote'),
        duplicate.deliveryNoteId
      )
    );
  };
  return (
    <Card
      isExpandable
      hasSeparator
      variant="inlineGrey"
      title={i18n.t('entity.warehouse.labels.basicInformation')}
    >
      <VStack spacing={4}>
        <Show when={hasSuppliersLoaded && not(doesSupplierExist)}>
          <Alert
            type="inline"
            variant="error"
            title={`${i18n.t('entity.warehouse.notifications.supplierDoesNotExist')}.`}
            message={`${i18n.t('entity.warehouse.notifications.youHaveToCreateNewSupplier')}.`}
            hyperlinks={[
              {
                isDisabled: suppliersQuery.isFetching,
                title: i18n.t('general.actions.refresh'),
                leftIcon: 'navigation/refresh',
                onClick: () => {
                  pushGtmEvent({
                    event: 'button_click',
                    event_id: 'ai_tool_dn_refresh_suppliers',
                  });
                  suppliersQuery.refetch();
                },
              },
            ]}
          />
        </Show>
        <Show when={hasDuplicateDeliveryNote}>
          <Alert
            type="inline"
            variant="warning"
            title={`${i18n.t('entity.documentExtraction.notifications.duplicateDeliveryNote')}.`}
            message={`${i18n.t('entity.documentExtraction.notifications.duplicateDeliveryNoteMessage')}.`}
            hyperlinks={getDuplicateDeliveryNoteHyperLinks(duplicateDeliveryNote)}
          />
        </Show>
        <FormField
          control={props.control}
          type="hidden"
          name="basicInformation.duplicateDeliveryNote"
        />
        <Grid columns={8}>
          <GridItem span={isCollapsed ? 2 : 4}>
            <FormField
              autoFocus
              isRequired
              isLoading={suppliersQuery.isFetching}
              isNotClearable
              isInvalid={hasSuppliersLoaded && not(doesSupplierExist)}
              control={props.control}
              name="basicInformation.supplier"
              type="choice"
              label={i18n.t('entity.warehouse.labels.supplier')}
              options={getOptionsFromCustomersAsSupplier(suppliersQuery.data)}
              onChange={handleSupplierChange}
            />
          </GridItem>
          <GridItem span={2}>
            <FormField
              isRequired
              isLoading={areWarehousesFetching}
              isNotClearable
              control={props.control}
              name="basicInformation.warehouse"
              type="choice"
              label={i18n.t('entity.warehouse.labels.warehouse')}
              options={getOptionsFromWarehouses(availableWarehousesToSupplier)}
              onChange={handleWarehouseChange}
            />
          </GridItem>
          <GridItem span={2}>
            <FormField
              isRequired
              isDisabled
              isLoading={
                tenantQuery.isLoading || authorizationProfilesQuery.isLoading || isWarehouseFetching
              }
              isNotClearable
              control={props.control}
              name="basicInformation.authorizationProfile"
              type="choice"
              label={i18n.t('entity.warehouse.labels.authorizationProfile')}
              options={getOptionsFromAuthorizationProfiles(
                authorizationProfilesQuery.data,
                selectedAuthorizationProfile
              )}
            />
          </GridItem>
          <GridItem span={2}>
            <FormField
              isAutoFocused={hasDuplicateDeliveryNote}
              control={props.control}
              name="basicInformation.deliveryNoteNumber"
              isDisabled={behaviour.isDocumentIdentityLocked}
              type="text"
              label={i18n.t('entity.warehouse.labels.deliveryNoteNumber')}
            />
          </GridItem>
          <GridItem span={2}>
            <FormField
              control={props.control}
              name="basicInformation.invoiceNumber"
              isDisabled={behaviour.isDocumentIdentityLocked}
              type="text"
              label={i18n.t('entity.warehouse.labels.invoiceNumber')}
            />
          </GridItem>

          <GridItem span={2}>
            <FormField
              isRequired
              isLoading={paymentTypesQuery.isLoading}
              isNotClearable
              control={props.control}
              name="basicInformation.paymentType"
              type="choice"
              label={i18n.t('entity.warehouse.labels.paymentType')}
              options={getOptionsFromPaymentTypes(paymentTypesQuery.data)}
            />
          </GridItem>
          <GridItem span={2}>
            <FormField
              isDisabled
              isRequired
              isLoading={currenciesQuery.isLoading}
              isNotClearable
              control={props.control}
              name="basicInformation.currency"
              type="choice"
              label={i18n.t('entity.warehouse.labels.currency')}
              options={getOptionsFromCurrencies(currenciesQuery.data)}
            />
          </GridItem>
        </Grid>

        <HStack>
          <FormButton
            isDisabled={not(doesSupplierExist) || areWarehousesFetching || isWarehouseFetching}
            control={props.control}
            type="submit"
            title={i18n.t('general.actions.confirm')}
            onClick={() => {
              pushGtmEvent({
                event: 'button_click',
                event_id: 'ai_tool_dn_confirm_header',
              });
            }}
          />
        </HStack>
      </VStack>
    </Card>
  );
}
